import { useCallback, useEffect, useState } from 'react';

const NOTION_TOKEN_KEY = 'pinepomo-notion-token';
const NOTION_DB_KEY = 'pinepomo-notion-database-id';

interface UseNotionAuthReturn {
  token: string | null;
  databaseId: string | null;
  isConnected: boolean;
  setToken: (token: string) => void;
  setDatabaseId: (id: string) => void;
  disconnect: () => void;
  validateConnection: (token: string, databaseId: string) => Promise<boolean>;
  isValidating: boolean;
}

export function useNotionAuth(): UseNotionAuthReturn {
  const [token, setTokenState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(NOTION_TOKEN_KEY);
  });
  const [databaseId, setDatabaseIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(NOTION_DB_KEY);
  });
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    if (token) {
      localStorage.setItem(NOTION_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(NOTION_TOKEN_KEY);
    }
  }, [token]);

  useEffect(() => {
    if (databaseId) {
      localStorage.setItem(NOTION_DB_KEY, databaseId);
    } else {
      localStorage.removeItem(NOTION_DB_KEY);
    }
  }, [databaseId]);

  const validateConnection = useCallback(
    async (testToken: string, testDbId: string): Promise<boolean> => {
      if (!testToken.trim() || !testDbId.trim()) return false;

      setIsValidating(true);
      try {
        const { notionProxy } = await import('@/lib/notionProxy');
        const result = await notionProxy({
          token: testToken,
          action: 'query-tasks',
          databaseId: testDbId,
        });
        return !result.error;
      } catch {
        return false;
      } finally {
        setIsValidating(false);
      }
    },
    []
  );

  const setToken = useCallback((t: string) => {
    setTokenState(t.trim() || null);
  }, []);

  const setDatabaseId = useCallback((id: string) => {
    setDatabaseIdState(id.trim() || null);
  }, []);

  const disconnect = useCallback(() => {
    setTokenState(null);
    setDatabaseIdState(null);
  }, []);

  return {
    token,
    databaseId,
    isConnected: !!token && !!databaseId,
    setToken,
    setDatabaseId,
    disconnect,
    validateConnection,
    isValidating,
  };
}
